import { escapeHtml } from "./visitorsFormat";
import { HappyPathFacts } from "./happyPath";

/*
 * THE FLOW LINES' TOOLTIPS, in the app's own tooltip style (Chris, 2026-10-18: "Can we improve the flow line tooltips to match the style of our other tooltips? That goes for the happy path red
 * line tooltip also."). They used to be a plain white box of `UPPER-CASED LABEL : value` lines, the one tooltip left in the old style. Now they are the same card as a bubble's: a coloured
 * background with white text, a two-column header (the headline numbers), and rows of a label and a bold value with a faint sub-value (assets/theme/Styles.scss, `tooltip-v2` and its
 * `flow-tooltip` modifier). Pure (no DOM, no d3), so every sentence and every number can be tested. The colour is the line's own: the flow lines' blue, the most common path's red, each darkened
 * until white text reads on it.
 *
 * Every page name goes through escapeHtml: names come from visitors' own address bars and the tooltip's markup is HTML. (The old route tooltip put them in unescaped.)
 */

/** the flow lines' blue (rgba(80,120,200)) and the most common path's red (#f4364c), each darkened by 26% so white text reads on them */
export const FLOW_TOOLTIP_BACKGROUND = "#3b5994";
export const HAPPY_PATH_TOOLTIP_BACKGROUND = "#b52838";

/** the gap between the pointer and the card, so the card never sits under the cursor it was opened by */
export const FLOW_TOOLTIP_OFFSET = 14;

const count = (value: number): string => Math.round(value).toLocaleString("en-US");

/** A share of a whole: whole percent from 10% up, one decimal below it ("1.6%", "42%"), "<0.1%" for something real but tiny, "0%" for none. */
export const share = (part: number, whole: number): string => {
  if (!(whole > 0) || !(part > 0)) return "0%";
  const v = Math.min(1, part / whole) * 100;
  if (v < 0.05) return "<0.1%";

  return v < 10 ? `${Number(v.toFixed(1))}%` : `${Math.round(v)}%`;
};

const header = (leftLabel: string, leftValue: string, rightLabel: string, rightValue: string, rightClass = ""): string => `
    <div class="tooltip-header">
      <div class="tooltip-header-col"><span class="tooltip-header-label">${escapeHtml(leftLabel)}</span><span class="tooltip-header-value">${leftValue}</span></div>
      <div class="tooltip-header-col"><span class="tooltip-header-label">${escapeHtml(rightLabel)}</span><span class="tooltip-header-value ${rightClass}">${rightValue}</span></div>
    </div>`;

const row = (label: string, value: string, sub = "", key = ""): string =>
  `<div class="tooltip-row"><span class="tooltip-row-label">${key}${label}</span><span class="tooltip-row-value">${value}${sub ? `<span class="tooltip-row-sub">${sub}</span>` : ""}</span></div>`;

const key = (opacity: number) => `<i class="tooltip-flow-key" style="opacity:${opacity}"></i>`;

/**
 * One route between two boxes: how much TRAFFIC there was between these two pages: the times visitors went from one to the other (both ways), its share of all the traffic counted, and the split between the two directions. One session that goes
 * /docs, /blog, /docs, /blog is THREE of them (counted 2026-10-25 from flowCounts: [2, 1] for that one session), so it is not a count of sessions. Wording by Chris, 2026-10-25: "Moves" was a word Skydive
 * uses nowhere else, "Page views" did not say what happens BETWEEN the two boxes, and "Sessions" was simply wrong here. He wanted something like 'Page view traffic between these two pages', shorter: the
 * header says "Visits between" (Chris: "Visits between for the in between traffic", and "New visits in" for a landing) and the other column "% of traffic". Every card also says what period its numbers
 * cover ("Last 30 Days"), as a faint line at the bottom.
 */
export interface RouteTip {
  /** the two page names, in the order of the busier direction (a tie reads low to high, as the line is drawn) */
  from: string;
  to: string;
  /** times visitors went from `from` to `to`, and the other way */
  forward: number;
  backward: number;
  /** the route's total, as the line counts it (both directions together) */
  total: number;
  /** all the traffic the flows count: each visit's first page and every step between boxes */
  allTraffic: number;
  /** set when the counts cover only part of the range (the server's cap): what they cover, in words */
  basedOn?: string;
  /** the period the numbers cover, as the Historical filter names it ("Last 30 Days") */
  period?: string;
}

export const routeTooltipHtml = (t: RouteTip): string => {
  const from = escapeHtml(t.from);
  const to = escapeHtml(t.to);
  const split = t.forward + t.backward;
  const denominator = split > 0 ? split : t.total;
  const bar =
    split > 0
      ? `<div class="tooltip-flow-bar"><span style="flex:${t.forward};opacity:1"></span><span style="flex:${t.backward};opacity:0.45"></span></div>`
      : "";

  return `${header("Visits between", count(t.total), "% of traffic", share(t.total, t.allTraffic))}
    ${bar}
    <div class="tooltip-body">
      ${row(`${from} → ${to}`, count(t.forward), share(t.forward, denominator), key(1))}
      ${row(`${to} → ${from}`, count(t.backward), share(t.backward, denominator), key(0.45))}
    </div>${periodNote(t.period)}${basedOnNote(t.basedOn)}`;
};

/** The line from the "new visitors" anchor to a landing page (a one-way flow: nobody goes the other way). */
export interface ArrivalTip {
  to: string;
  /** each landing is a new visit starting on this page (Chris, 2026-10-25: 'New visits in') */
  sessions: number;
  /** ALL the visits that landed anywhere (the sum of every landing line): a landing's share is of visits, the same whole Insights uses, not of landings plus moves */
  allTraffic: number;
  /** set when the counts cover only part of the range: what they cover, in words */
  basedOn?: string;
  period?: string;
}

/**
 * The period a flow card's numbers cover, the way the Historical filter names it. A replay says WHICH day as well (Chris, 2026-10-25: "I ran an Oct 7th playback and the tooltip says 'Since 12:00 am' but it
 * doesn't say which day"); a shared clip's own label already carries its day. A custom range reads "Since Oct 1".
 */
export const flowPeriodLabel = (o: { inPlayback: boolean; priorLabel?: string | null; playbackDate?: string | null; range?: { name?: string; value?: any } | null }): string => {
  if (o.inPlayback) {
    const label = o.priorLabel || "Since 12:00 AM";
    if (label.includes("·")) return label;
    const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(o.playbackDate || ""));

    return day ? `${label} · ${new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : label;
  }
  const range = o.range;
  if (!range?.name) return "";
  if (range.name === "Custom Date") return `Since ${new Date(range.value).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

  return range.name;
};

/** the faint line that says the numbers above are not the whole range (a count that does not say so reads as the whole range) */
/** the period the numbers cover, a faint line at the foot of the card: a count that does not say over what is easy to misread */
const periodNote = (period?: string): string => (period ? `\n    <div class="tooltip-note faint">${escapeHtml(period)}</div>` : "");

const basedOnNote = (basedOn?: string): string => (basedOn ? `\n    <div class="tooltip-note faint">Based on ${escapeHtml(basedOn)}</div>` : "");

export const arrivalTooltipHtml = (t: ArrivalTip): string => `${header("New visits in", count(t.sessions), "% of visits", share(t.sessions, t.allTraffic))}
    <div class="tooltip-body">
      ${row(`New visitors → ${escapeHtml(t.to)}`, count(t.sessions), "100%", key(1))}
    </div>${periodNote(t.period)}${basedOnNote(t.basedOn)}`;

/**
 * The most common path (the red line): where it starts and ends in the header, each hop and how many went that way as rows, and in plain words why it ends there and what the numbers
 * cover. The words say what visits DID, never why they did it (docs/MESSAGING.md: Skydive shows behaviour, people infer cause), which is why nothing here says "dropped" or "lost".
 */
export const happyPathTooltipHtml = (f: HappyPathFacts): string => `${header("Starts at", escapeHtml(f.start), "Ends at", escapeHtml(f.end), "tooltip-header-next")}
    <div class="tooltip-body">
      ${row(`Began at ${escapeHtml(f.start)}`, count(f.began.weight), `of ${count(f.began.total)} visits (${f.began.pct}%)`)}
      ${f.hops.map((h) => row(`${escapeHtml(h.from)} → ${escapeHtml(h.to)}`, count(h.weight), "went on")).join("\n      ")}
    </div>
    <div class="tooltip-note">${escapeHtml(f.why)}</div>
    <div class="tooltip-note faint">Based on ${escapeHtml(f.basedOn)}</div>`;
